/**
 * Постепенный переезд на новый сайт (лавка на svoyalavka.com). Посетитель получает номер в cookie,
 * и, если попал в эксперимент, уходит на тот же адрес нового сайта с номером в параметре: куки у
 * двух доменов не общие. Новый сайт кладёт номер к себе и убирает его из адреса.
 *
 * Кого переводим: номер посетителя от 0 до 99 живёт в cookie, а не готовый вариант. Номер меньше
 * процента из админки — новый сайт. Поэтому при подъёме с 5 до 10 % добавляются номера 5–9, а те,
 * кто уже там, там и остаются. При 0 % cookie не ставим. Выбор «вернуться» важнее процента. Боты
 * всегда здесь: новый сайт закрыт от поиска. Вошедшие — здесь, пока в админке не включат их.
 * Секретная cookie new-site=1 переводит при любом проценте — проверить своими глазами.
 *
 * Без Next и браузера: файл берут middleware и тесты.
 */

export const NEW_SITE_ORIGIN_DEFAULT = 'https://marina-kambur.svoyalavka.com';

/** Секретная cookie: new-site=1 — перевести на новый сайт, что бы ни было дальше в правилах */
export const TEST_COOKIE = 'new-site';
export const AB_ID_COOKIE = 'ab_id';
/** Номер посетителя от 0 до 99: меньше процента — новый сайт */
export const AB_BUCKET_COOKIE = 'ab_bucket';
/** Группа для сводки: old или new. Браузер передаёт её в заказ (abOrderParams) */
export const AB_GROUP_COOKIE = 'ab_group';
/** Выбор посетителя: old — вернулся и больше не переводим, new — передумал */
export const AB_CHOICE_COOKIE = 'ab_choice';

export const AB_ID_PARAM = 'ab_id';
export const AB_CHOICE_PARAM = 'ab_choice';

const AB_ID =
  /^abt?-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const isAbId = (value: unknown): value is string =>
  typeof value === 'string' && AB_ID.test(value);

export const newAbId = () => `ab-${crypto.randomUUID()}`;

/** Своя проверка по секретной cookie: «abt-» всё проходит так же, но в сводку не идёт. */
export const newTestAbId = () => `abt-${crypto.randomUUID()}`;

export const newBucket = () => Math.floor(Math.random() * 100);

/** Номер из cookie; нет или мусор — null. */
export const parseBucket = (value: unknown) =>
  typeof value === 'string' && /^\d{1,2}$/.test(value) ? Number(value) : null;

export type NewSiteConfig = { percent: number; includeLoggedIn: boolean };

/** Ответ Strapi; странный ответ — null, чтобы не перевести лишних. */
export function parseNewSiteConfig(data: unknown): NewSiteConfig | null {
  const { percent, includeLoggedIn } = (data ?? {}) as Record<string, unknown>;
  if (
    !Number.isInteger(percent) ||
    (percent as number) < 0 ||
    (percent as number) > 100
  )
    return null;
  return {
    percent: percent as number,
    includeLoggedIn: includeLoggedIn === true,
  };
}

/**
 * Боты, проверки и превью ссылок. Пустой User-Agent — тоже не человек. Браузер Яндекса
 * (YaBrowser) и его приложение (YandexSearch) сюда не попадают.
 */
const BOT =
  /bot|crawl|spider|slurp|preview|lighthouse|pagespeed|headless|facebookexternalhit|vkshare|whatsapp|viber|yandex(?!search)|curl|wget|python|axios|node-fetch|go-http|okhttp|java\/|uptime|monitor/i;

export const isBot = (userAgent: string | null) =>
  !userAgent || BOT.test(userAgent);

/** Есть сессия next-auth, в том числе разбитая на части (.0, .1): человек вошёл. */
export const hasSession = (cookieNames: string[]) =>
  cookieNames.some((name) => name.includes('next-auth.session-token'));

export const isAbChoice = (value: unknown): value is 'old' | 'new' =>
  value === 'old' || value === 'new';

/**
 * Только витрина: главная, каталог, мастер-класс, «О себе», контакты. Корзина, кабинет, вход,
 * оплата, админка и форма отзыва из письма (…/add-review) остаются здесь, блог лавка не переносит.
 */
const STOREFRONT =
  /^\/(?:masterclasses(?:\/category\/[^/]+|\/[^/]+)?|course|about|contacts)?\/?$/;

export const isStorefrontPath = (pathname: string) => STOREFRONT.test(pathname);

/** Человек открывает страницу, а не Next подгружает данные перехода или заранее. */
export function isPageNavigation(
  method: string,
  headers: Pick<Headers, 'get'>,
) {
  if (method !== 'GET' && method !== 'HEAD') return false;
  if (headers.get('rsc') || headers.get('next-router-prefetch')) return false;
  if (headers.get('purpose') === 'prefetch') return false;
  const dest = headers.get('sec-fetch-dest');
  return !dest || dest === 'document';
}

/** Тот же адрес на новом сайте: путь без слэша на конце (там без него), параметры целы. */
export function newSiteUrl(
  origin: string,
  pathname: string,
  search: string,
  id: string,
) {
  const url = new URL(pathname.replace(/\/+$/, '') || '/', origin);
  url.search = search;
  url.searchParams.set(AB_ID_PARAM, id);
  return url;
}

/** Параметры заказа: из какой группы покупатель. Не участник — пусто. */
export function abOrderParams(cookies: string) {
  const read = (name: string) =>
    cookies
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`))
      ?.slice(name.length + 1);
  const abId = read(AB_ID_COOKIE);
  const abGroup = read(AB_GROUP_COOKIE);
  return isAbId(abId) && (abGroup === 'old' || abGroup === 'new')
    ? { abId, abGroup }
    : {};
}
